import * as React from 'react';
import DOMPurify from 'dompurify';
import type { ZohoNoteEntry } from 'shared/src/api/ai-line-item-chat-types';
import { InfoPanelFrame } from './info-panel-frame';

type Props = {
  notes: ZohoNoteEntry[];
};

const INITIAL_VISIBLE = 3;

export function ZohoNotesCard({ notes }: Props) {
  const [showAll, setShowAll] = React.useState(false);

  if (notes.length === 0) return null;

  const visible = showAll ? notes : notes.slice(0, INITIAL_VISIBLE);
  const hiddenCount = notes.length - visible.length;

  return (
    <InfoPanelFrame
      title="Zoho Notes"
      detail={`${notes.length} ${notes.length === 1 ? 'note' : 'notes'}`}>
      <ul className="space-y-3">
        {visible.map(note => (
          <NoteEntry key={note.id} note={note} />
        ))}
      </ul>

      {hiddenCount > 0 ? (
        <button
          type="button"
          className="mt-3 text-xs font-medium text-primary hover:underline"
          onClick={() => setShowAll(true)}>
          Show {hiddenCount} more {hiddenCount === 1 ? 'note' : 'notes'}
        </button>
      ) : null}
      {showAll && notes.length > INITIAL_VISIBLE ? (
        <button
          type="button"
          className="mt-3 text-xs font-medium text-primary hover:underline"
          onClick={() => setShowAll(false)}>
          Show less
        </button>
      ) : null}
    </InfoPanelFrame>
  );
}

/* ---------------------------------------------------------------------------
 * Private helpers
 * --------------------------------------------------------------------------- */

function NoteEntry({ note }: { note: ZohoNoteEntry }) {
  const [expanded, setExpanded] = React.useState(false);
  const [overflowing, setOverflowing] = React.useState(false);
  const bodyRef = React.useRef<HTMLDivElement | null>(null);

  const sanitized = note.content_html ? sanitizeHtml(note.content_html) : null;
  const title = note.title?.trim() || null;
  const stamp = formatStamp(note.modified_time ?? note.created_time);
  const author = note.author_name?.trim() || null;

  React.useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || expanded) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [sanitized, expanded]);

  return (
    <li className="border-l-2 border-gray-200 pl-3">
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0 text-xs text-gray-700">
          {author ? <span className="font-medium text-gray-900">{author}</span> : null}
          {title ? (
            <span className={author ? 'ml-2 text-gray-700' : 'font-medium text-gray-900'}>
              {title}
            </span>
          ) : null}
        </div>
        <div className="shrink-0 text-[11px] text-gray-500">{stamp}</div>
      </div>
      {sanitized ? (
        <div>
          <div
            ref={bodyRef}
            className={`prose prose-sm mt-1 max-w-none text-xs text-gray-700 ${
              expanded ? 'max-h-[400px] overflow-y-auto' : 'line-clamp-4'
            }`}
            dangerouslySetInnerHTML={{ __html: sanitized }}
          />
          {overflowing || expanded ? (
            <button
              type="button"
              className="mt-1 text-[11px] font-medium text-primary hover:underline"
              onClick={() => setExpanded(prev => !prev)}>
              {expanded ? 'Show less' : 'Show more'}
            </button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function formatStamp(iso: string | null): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

const SANITIZE_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'a', 'span'],
  ALLOWED_ATTR: ['href', 'rel', 'class'],
  FORBID_ATTR: ['style', 'onerror', 'onload']
};

function sanitizeHtml(html: string): string {
  DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  const result = DOMPurify.sanitize(html, SANITIZE_CONFIG);
  DOMPurify.removeHook('afterSanitizeAttributes');
  return result;
}
